import type { LogoData, NavigationItemData } from '@beat-venues/common-types';
import dynamic from 'next/dynamic';
import { useTranslation } from 'next-i18next';
import { MouseEvent, ReactNode, useEffect, useState, type JSX } from 'react';
import { createPortal } from 'react-dom';
import { type VariantProps, tv } from 'tailwind-variants';

import animation from '../../../public/images/afas-logo-animation.json';
import {
    Container,
    HamburgerSolidIcon,
    InputField,
    Logo,
    SearchSolidIcon,
    Text,
} from '../../atoms';
import { ButtonOrLink } from '../../atoms/button-or-link';
import { NavigationList } from '../../molecules';
import type { CommonProps } from '../../types';

export interface NavBarProps
    extends CommonProps,
        VariantProps<typeof NavBarStyles> {
    logo: LogoData;
    items?: NavigationItemData[];
    onOpenMenu?: () => void;
    onSearch: (value?: string) => void;
    onShowMobileSearch: (value: boolean) => void;
    customTranslation?: boolean;
    customTranslationPlaceholder?: string;
    isMobile: boolean;
    mobileUserAuth?: ReactNode;
}

export const NavBarStyles = tv({
    slots: {
        root: 'py-3 z-0 w-full lg:pt-3 bg-black lg:bg-gray-700',
        mobileSearch:
            'flex h-[118px] w-full items-center justify-center bg-black lg:hidden',
    },
    variants: {
        searchMobileIsVisible: {
            true: { root: 'invisible lg:visible' },
            false: {},
        },
    },
});

export const NavBar = ({
    logo,
    items,
    onOpenMenu,
    onSearch,
    onShowMobileSearch,
    customTranslation,
    customTranslationPlaceholder,
    className,
    isMobile,
    mobileUserAuth,
    testId = 'nav-bar',
    ...props
}: NavBarProps): JSX.Element => {
    const DynamicLottie = dynamic(() => import('lottie-react'), { ssr: false });

    const { t } = useTranslation('global');
    const [searchElement, setSearchElement] = useState<Element | null>(null);
    const [searchMobileIsVisible, setSearchMobileIsVisible] = useState(false);

    const { root, mobileSearch } = NavBarStyles();

    useEffect(() => {
        setSearchElement(document.querySelector('[data-portal-root=search]'));
    }, []);

    const handleClick = () => {
        setSearchMobileIsVisible(true);
        onShowMobileSearch(searchMobileIsVisible);

        let prevScrollPosition = window.pageYOffset;

        window.addEventListener('scroll', () => {
            const currentScrollPosition = window.pageYOffset;
            if (currentScrollPosition > prevScrollPosition) {
                setSearchMobileIsVisible(false);
                onShowMobileSearch(!searchMobileIsVisible);
            }

            prevScrollPosition = currentScrollPosition;
        });
    };

    const handleClose = (event: MouseEvent<HTMLDivElement>) => {
        if (event.target === event.currentTarget) {
            setSearchMobileIsVisible(false);
            onShowMobileSearch(searchMobileIsVisible);
        }
    };

    const renderSearchField = () => {
        return (
            <InputField
                className={`${
                    searchMobileIsVisible ? 'bg-black' : 'hidden lg:block'
                }`}
                clearOnSubmit={true}
                icon={<SearchSolidIcon size="md" />}
                label={`${t('inputPlaceholders.search', {
                    ns: 'global',
                })} navbar`}
                name="searchNavbar"
                placeholder={
                    customTranslation
                        ? customTranslationPlaceholder
                        : t('inputPlaceholders.search', { ns: 'global' }) || ''
                }
                type="search"
                onSubmit={onSearch}
            />
        );
    };

    if (searchMobileIsVisible && searchElement) {
        return createPortal(
            <div className={mobileSearch()} onClick={handleClose}>
                <div
                    className={`${
                        searchMobileIsVisible ? ' w-full px-3' : 'hidden'
                    }`}
                >
                    {renderSearchField()}
                </div>
            </div>,
            searchElement
        );
    }

    return (
        <nav
            aria-label="Main"
            className={root({ className, searchMobileIsVisible })}
            data-testid={testId}
            role="navigation"
            {...props}
        >
            <Container className="flex justify-between gap-8 px-6 lg:gap-0 lg:px-4">
                {/* This should be revert to the original logo after Afas is launched */}
                {logo.venueId === 'sportpaleis' ? (
                    <ButtonOrLink href={logo.href}>
                        <span aria-hidden="true">
                            <DynamicLottie
                                animationData={animation}
                                loop={true}
                                style={{ height: 75, maxWidth: 222 }}
                            />
                        </span>

                        <Text className="sr-only">
                            {t('goToHomepage', {
                                ns: 'global',
                            }) ?? undefined}
                        </Text>
                    </ButtonOrLink>
                ) : (
                    <Logo
                        className="relative h-[60px] lg:h-[66px]"
                        href={logo.href}
                        overwriteScreenreaderText={
                            t('goToHomepage', {
                                ns: 'global',
                            }) ?? undefined
                        }
                        venueId={logo.venueId}
                    />
                )}

                {items && <NavigationList items={items} />}
                <div className="relative flex max-h-16 flex-row place-items-center gap-5 lg:gap-0">
                    {renderSearchField()}
                    {isMobile && (
                        <>
                            {mobileUserAuth}
                            <ButtonOrLink
                                aria-expanded={searchMobileIsVisible}
                                onClick={() => {
                                    handleClick();
                                }}
                            >
                                <Text className="sr-only">
                                    {t('searchLabel', {
                                        ns: 'global',
                                    })}
                                </Text>
                                <SearchSolidIcon size="lg" />
                            </ButtonOrLink>
                            <div>
                                <ButtonOrLink
                                    aria-expanded="false"
                                    onClick={onOpenMenu}
                                >
                                    <Text className="sr-only">
                                        {t('openMenu', {
                                            ns: 'global',
                                        })}
                                    </Text>
                                    <HamburgerSolidIcon size="lg" />
                                </ButtonOrLink>
                            </div>
                        </>
                    )}
                </div>
            </Container>
        </nav>
    );
};
